/*
 * ⌥⌘1–4 (Ctrl+Alt+1–4) make one pane big, left to right: Case Files, Work
 * Products, the open document, Casey. The same chord again, or Escape,
 * restores the layout. Digits rather than letters: ⌥⌘ with F, W, D or H is
 * taken by Safari or macOS, and ⌘1–9 / Ctrl+1–9 switch browser tabs.
 *
 * Rich-text editors bind ⌥⌘1–3 to headings, so the chords stand down inside
 * a contenteditable. In inputs they are safe on a Mac (⌥⌘ types nothing) but
 * not elsewhere, where Ctrl+Alt is AltGr and types characters.
 */
import { useEffect } from "react";

import { isApplePlatform } from "@/features/globalSearch/GlobalSearchTrigger";

import { ideLayoutStore, type IdeFocus } from "./ideLayoutStore";

type ChordEvent = Pick<
  KeyboardEvent,
  "code" | "key" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey" | "repeat"
> & { target: EventTarget | null; defaultPrevented: boolean };

const FOCUS_DIGIT: Record<IdeFocus, string> = {
  files: "1",
  workspace: "2",
  document: "3",
  casey: "4"
};

const FOCUS_BY_CODE: Record<string, IdeFocus> = Object.fromEntries(
  (Object.entries(FOCUS_DIGIT) as [IdeFocus, string][]).map(([f, d]) => [
    `Digit${d}`,
    f
  ])
);

/** What each focus mode is called on its control and in its tooltip. */
export const FOCUS_LABEL: Record<IdeFocus, string> = {
  files: "Maximize Case Files",
  workspace: "Maximize Work Products",
  document: "Maximize Document",
  casey: "Maximize Casey"
};

export const paneShortcutLabel = (focus: IdeFocus): string =>
  isApplePlatform()
    ? `⌥⌘${FOCUS_DIGIT[focus]}`
    : `Ctrl+Alt+${FOCUS_DIGIT[focus]}`;

export const paneShortcutKeys = (focus: IdeFocus): string =>
  isApplePlatform()
    ? `Meta+Alt+${FOCUS_DIGIT[focus]}`
    : `Control+Alt+${FOCUS_DIGIT[focus]}`;

const editable = (target: EventTarget | null): "rich" | "field" | null => {
  if (!(target instanceof HTMLElement)) return null;
  if (target.isContentEditable) return "rich";
  if (target.closest("input, textarea, select")) return "field";
  return null;
};

/** Another modal, menu or popover, or the draft's own full-window focus, has Escape. */
const overlayOpen = (): boolean =>
  Boolean(
    document.querySelector(
      '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="menu"][data-state="open"], [role="listbox"][data-state="open"], [data-draft-expanded]'
    )
  );

/** The focus mode a key event asks for, if it is one of the chords. */
export const focusForShortcut = (e: ChordEvent): IdeFocus | null => {
  const focus = FOCUS_BY_CODE[e.code];
  if (!focus) return null;
  if (!(e.metaKey || e.ctrlKey) || !e.altKey || e.shiftKey || e.repeat)
    return null;
  const field = editable(e.target);
  if (field === "rich") return null;
  if (field === "field" && !isApplePlatform()) return null;
  return focus;
};

/** Escape restores a focus unless something nearer took it. */
export const isRestoreKey = (e: ChordEvent): boolean =>
  e.key === "Escape" &&
  !e.metaKey &&
  !e.ctrlKey &&
  !e.altKey &&
  !e.shiftKey &&
  !e.defaultPrevented &&
  editable(e.target) === null &&
  !overlayOpen();

/** Double-clicking a pane header's empty space toggles its focus. */
export const toggleFocusFromHeader = (
  e: { target: EventTarget | null },
  focus: IdeFocus
): void => {
  if (
    e.target instanceof Element &&
    e.target.closest("button, a, input, [role='menuitem']")
  )
    return;
  ideLayoutStore.toggleFocus(focus);
};

/** Bind the chords and Escape while the IDE is mounted. */
export const usePaneShortcuts = (): void => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const focus = focusForShortcut(e);
      if (focus) {
        e.preventDefault();
        ideLayoutStore.toggleFocus(focus);
        return;
      }
      if (ideLayoutStore.isFocused() && isRestoreKey(e)) {
        e.preventDefault();
        ideLayoutStore.restore();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
};
